<script lang="ts">
  import { createEventDispatcher, onDestroy } from 'svelte';
  import type { Writable } from 'svelte/store';
  import XIcon from '$icons/XIcon.svelte';
  import CornerSquare from './wrappers/CornerSquare.svelte';
  import DotGrid from './DotGrid.svelte';
  import { browser } from '$app/environment';

  export let modalOpen: boolean | Writable<boolean>;
  export let title = '';
  export let clickoutCloseDisabled = false;
  export let allowExit = true;
  export let allowScroll = false;
  export let size: 'sm' | 'md' | 'lg' = 'sm';
  export let maxH: string | undefined = undefined;
  export let warning = false;

  let isOpen = false;
  let unsubscribe: () => void;

  const dispatch = createEventDispatcher();

  $: if (typeof modalOpen === 'object' && 'subscribe' in modalOpen) {
    unsubscribe = modalOpen.subscribe((value) => {
      isOpen = value;
    });
  } else {
    isOpen = modalOpen;
  }

  function setBodyOverflow(value: string) {
    if (browser) {
      document.body.style.overflow = value;
    }
  }

  onDestroy(() => {
    if (unsubscribe) {
      unsubscribe();
    }
    setBodyOverflow('');
  });

  function closeModal() {
    if (typeof modalOpen === 'object' && 'subscribe' in modalOpen) {
      modalOpen.set(false);
    } else {
      isOpen = false;
      modalOpen = false;
    }
  }

  $: if (isOpen) {
    setBodyOverflow('hidden');
    dispatch('open');
  } else {
    setBodyOverflow('');
    dispatch('close');
  }
</script>

{#if isOpen}
  <!-- svelte-ignore a11y-click-events-have-key-events -->
  <div class="relative z-[100] {$$props.class}">
    <!-- svelte-ignore a11y-click-events-have-key-events -->
    <!-- svelte-ignore a11y-no-static-element-interactions -->
    <div
      class="fixed inset-0 z-[101] flex flex-col items-center justify-center bg-black/80"
      on:click={() => {
        if (!clickoutCloseDisabled) {
          closeModal();
        }
      }}
    >
      <CornerSquare class="flex !h-fit flex-col {size == 'sm' ? '!w-[372px]' : '!w-[616px]'}">
        <div
          class="gap-x-md border-gray-dark-2 bg-gray-dark-1 px-lg py-md flex w-full items-center overflow-hidden border-b-2"
          class:bg-orange-dark-4={warning}
        >
          <h2
            class="text-normal font-architekt text-gray-dark-12 whitespace-nowrap text-lg leading-6"
            class:text-orange-dark-11={warning}
          >
            {title}
          </h2>
          <DotGrid {warning} />
          {#if allowExit}
            <button on:click={closeModal}>
              <XIcon class={warning ? 'text-orange-dark-11' : 'text-gray-dark-11'} />
            </button>
          {/if}
        </div>
        <div
          class:overflow-y-scroll={allowScroll}
          class="bg-gray-dark-0 px-2xl py-xl w-full {maxH ? `max-h-[${maxH}]` : ''}"
        >
          <slot />
        </div>
      </CornerSquare>
    </div>
  </div>
{/if}
